import React, { useEffect, useMemo, useState } from 'react';
import BackspaceOutlinedIcon from '@mui/icons-material/BackspaceOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Pagination,
  Paper,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import PropTypes from 'prop-types';

import {
  analyzeAudience,
  DESCARTAR_GRUPO,
  resolveAudience,
} from './audienceValidation';

import styles from './AudienceIssuesDialog.module.css';

const GRUPOS_POR_PAGINA = 20;

/** Marcador para saber de un vistazo que version esta desplegada. Subir al cambiar. */
export const VERSION_UI = 'v8';

const ORIGEN_LABEL = {
  excel: 'Excel',
  formulario: 'Formulario',
};

const CAMPO_LABEL = {
  documento: 'Documento',
  correo: 'Correo',
  celular: 'Celular',
  indicativo: 'Indicativo',
  uidEmpresa: 'UidEmpresa',
  uid2: 'Uid2',
  uid3: 'Uid3',
};

/** Estados en rojo: no se carga nada hasta arreglarlos o descartarlos. */
const ESTADOS_PENDIENTES = ['invalido', 'error'];

const FILTROS = [
  { id: 'todas', label: 'Todas' },
  { id: 'invalido', label: 'Dato inválido' },
  { id: 'error', label: 'Sin identificar' },
  { id: 'advertencia', label: 'Con advertencia' },
  { id: 'descartada', label: 'Descartadas' },
  { id: 'ok', label: 'Correctas' },
];

/** Estado visual de la fila; define el color y la etiqueta "Qué pasa". */
const estadoDe = (item, descartada) => {
  if (descartada) return 'descartada';
  if (item.accion === 'descarta') return 'error';
  if (item.camposSaneados?.length > 0) return 'invalido';
  if (item.mensajes.length > 0) return 'advertencia';
  return 'ok';
};

/**
 * En el resultado, lo que el backend mete en Fallidas no siempre es una fila
 * perdida: la que no tiene correo se carga igual (Procesadas++ ocurre antes de
 * mirar el correo) y solo se queda sin invitacion. Por eso no se dice
 * "Se descarta" ni "Resolver": manda el motivo que da el backend.
 */
const etiquetaEstado = (estado, esResultado) => {
  if (!esResultado) return ESTADO[estado].label;
  return estado === 'ok' ? 'Cargada' : 'Revisar';
};

const ESTADO = {
  error: { label: 'Resolver', color: 'error', clase: styles.filaError },
  invalido: { label: 'Resolver', color: 'error', clase: styles.filaError },
  advertencia: { label: 'Se carga', color: 'warning', clase: styles.filaAdvertencia },
  descartada: { label: 'Descartada', color: 'default', clase: styles.filaDescartada },
  ok: { label: 'Se carga', color: 'success', clase: '' },
};

/**
 * Input siempre visible. El modo edicion del DataGrid exige doble clic y no se
 * ve que la celda sea editable, asi que se dibuja el campo directamente.
 * El cambio se confirma al salir del campo o con Enter, no en cada tecla, para
 * no revalidar el archivo entero en cada pulsacion.
 */
const CeldaEditable = ({ value, disabled, invalido, onCommit }) => {
  const [texto, setTexto] = useState(value);

  useEffect(() => setTexto(value), [value]);

  return (
    <TextField
      variant="standard"
      size="small"
      fullWidth
      disabled={disabled}
      error={invalido}
      value={texto}
      inputProps={{ 'aria-label': 'editar' }}
      InputProps={
        invalido && !disabled
          ? {
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Vaciar la celda: el dato no sirve para identificar">
                    <IconButton
                      size="small"
                      aria-label="vaciar"
                      onClick={() => {
                        setTexto('');
                        onCommit('');
                      }}
                    >
                      <BackspaceOutlinedIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }
          : undefined
      }
      onChange={(e) => setTexto(e.target.value)}
      onBlur={() => {
        if (texto !== value) onCommit(texto);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.target.blur();
      }}
    />
  );
};

CeldaEditable.propTypes = {
  value: PropTypes.any,
  disabled: PropTypes.bool,
  invalido: PropTypes.bool,
  onCommit: PropTypes.func.isRequired,
};

CeldaEditable.defaultProps = { value: '', disabled: false, invalido: false };

const Toolbar = () => (
  <GridToolbarContainer>
    <GridToolbarExport
      csvOptions={{ fileName: 'detalle-audiencia', delimiter: ';', utf8WithBom: true }}
    />
  </GridToolbarContainer>
);

const Dato = ({ valor, prefijo }) =>
  valor ? (
    <span className={styles.filaDato}>
      {prefijo}
      {valor}
    </span>
  ) : (
    <span className={`${styles.filaDato} ${styles.filaDatoVacio}`}>—</span>
  );

Dato.propTypes = { valor: PropTypes.any, prefijo: PropTypes.string };
Dato.defaultProps = { valor: null, prefijo: '' };

const FilaResumen = ({ row }) => (
  <Box className={styles.filaResumen}>
    <Chip
      size="small"
      variant="outlined"
      label={`${ORIGEN_LABEL[row.origen]} · fila ${row.fila}`}
    />
    <Dato valor={row.documento} />
    <Dato valor={row.correo} />
    <Dato valor={row.celular} prefijo={row.celular ? `+${row.indicativo ?? 57} ` : ''} />
    <Dato valor={row.uidEmpresa} />
    <Dato valor={row.uid2} />
    <Dato valor={row.uid3} />
  </Box>
);

FilaResumen.propTypes = { row: PropTypes.object.isRequired };

const GrupoDuplicado = ({ grupo, seleccion, onChange }) => (
  <Paper variant="outlined" className={styles.grupo}>
    <Box className={styles.grupoClaves}>
      <Typography variant="subtitle2">
        {grupo.filas.length} filas describen a la misma persona:
      </Typography>
      {grupo.claves.map((clave) => (
        <Chip
          key={`${clave.campo}-${clave.valor}`}
          size="small"
          color="warning"
          variant="outlined"
          label={`${CAMPO_LABEL[clave.campo]}: ${clave.valor}`}
        />
      ))}
    </Box>

    <RadioGroup value={seleccion} onChange={(e) => onChange(grupo.id, e.target.value)}>
      {grupo.filas.map((row) => (
        <FormControlLabel
          key={row.key}
          value={row.key}
          control={<Radio size="small" />}
          label={<FilaResumen row={row} />}
        />
      ))}
      <FormControlLabel
        value={DESCARTAR_GRUPO}
        control={<Radio size="small" color="error" />}
        label={
          <Typography variant="body2" color="error">
            No cargar ninguna de estas {grupo.filas.length} filas
          </Typography>
        }
      />
    </RadioGroup>
  </Paper>
);

GrupoDuplicado.propTypes = {
  grupo: PropTypes.object.isRequired,
  seleccion: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

const AudienceIssuesDialog = ({
  open,
  modo,
  filasIniciales,
  analisisResultado,
  headerError,
  resumenBackend,
  isLoading,
  onCorregir,
  onConfirmar,
  onClose,
}) => {
  const esResultado = modo === 'resultado';
  // 'previo' es la revisión ANTES de cargar: se pinta igual que el resultado
  // -solo lectura, con lo que devolvió el backend- pero deja confirmar.
  const esPrevio = modo === 'previo';
  const soloLectura = esResultado || esPrevio;
  // El detalle viene recortado por el backend, asi que el total sale de sumarle
  // las que quedaron fuera de la lista.
  const totalConError = soloLectura
    ? (analisisResultado?.errors?.length ?? 0) + (resumenBackend?.fallidasOmitidas ?? 0)
    : 0;
  const conErrores = esPrevio && totalConError > 0;
  // Cuantas filas quedaron fuera del detalle. Sin decirlo, la lista de 200 se lee
  // como si fueran todas y el operador cree que hay 200 avisos, no 21.190.
  const recortadas =
    (resumenBackend?.fallidasOmitidas ?? 0) + (resumenBackend?.advertenciasOmitidas ?? 0);

  const [filas, setFilas] = useState(filasIniciales);
  const [descartadas, setDescartadas] = useState(() => new Set());
  const [selecciones, setSelecciones] = useState({});
  const [filtro, setFiltro] = useState('todas');
  // El resultado abre en el resumen. Abrir en la tabla de errores hacía creer que la
  // carga había fallado aunque se hubieran cargado miles de personas.
  const [pasoActual, setPasoActual] = useState(() => {
    if (modo !== 'resultado') return 0;
    return (analisisResultado?.groups?.length ?? 0) > 0 ? 2 : 1;
  });
  const [paginaGrupos, setPaginaGrupos] = useState(1);

  const analisisLocal = useMemo(() => analyzeAudience(filas), [filas]);
  const analisis = soloLectura ? analisisResultado : analisisLocal;
  const { detalle, groups } = analisis;



  // ── Edición en vivo ────────────────────────────────────────────
  const editarCelda = ({ id, field, value }) =>
    setFilas((prev) =>
      prev.map((f) => {
        if (`${f.origen}:${f.fila}` !== id) return f;
        if (field === 'indicativo') {
          const digitos = String(value ?? '').replace(/\D/g, '');
          return { ...f, indicativo: digitos === '' ? null : Number(digitos) };
        }
        const texto = String(value ?? '').trim();
        return { ...f, [field]: texto === '' ? null : texto };
      })
    );

  const alternarDescarte = (key) =>
    setDescartadas((prev) => {
      const siguiente = new Set(prev);
      if (siguiente.has(key)) siguiente.delete(key);
      else siguiente.add(key);
      return siguiente;
    });

  // ── Tabla de revisión ──────────────────────────────────────────
  const filasTabla = useMemo(
    () =>
      (detalle ?? []).map((item) => {
        const descartada = descartadas.has(item.key);
        return {
          id: item.key,
          estado: estadoDe(item, descartada),
          camposSaneados: item.camposSaneados ?? [],
          descartada,
          fila: item.fila,
          origen: item.origen,
          documento: item.row.documento ?? '',
          correo: item.row.correo ?? '',
          indicativo: item.row.indicativo ?? '',
          celular: item.row.celular ?? '',
          uidEmpresa: item.row.uidEmpresa ?? '',
          uid2: item.row.uid2 ?? '',
          uid3: item.row.uid3 ?? '',
          detalle: item.mensajes.join(' · '),
        };
      }),
    [detalle, descartadas]
  );

  const conteos = useMemo(
    () =>
      filasTabla.reduce(
        (acc, f) => ({ ...acc, todas: acc.todas + 1, [f.estado]: acc[f.estado] + 1 }),
        { todas: 0, invalido: 0, error: 0, advertencia: 0, descartada: 0, ok: 0 }
      ),
    [filasTabla]
  );

  const filasFiltradas =
    filtro === 'todas' ? filasTabla : filasTabla.filter((f) => f.estado === filtro);

  /** Filas que el usuario todavia tiene que arreglar o descartar. */
  const pendientes = useMemo(
    () => (soloLectura ? [] : filasTabla.filter((f) => ESTADOS_PENDIENTES.includes(f.estado))),
    [filasTabla, soloLectura]
  );

  const filasFinales = useMemo(() => {
    if (soloLectura) return [];
    const bloqueadas = new Set(pendientes.map((f) => f.id));
    return resolveAudience(analisisLocal, selecciones).filter(
      (r) => !descartadas.has(r.key) && !bloqueadas.has(r.key)
    );
  }, [analisisLocal, selecciones, descartadas, pendientes, soloLectura]);

  /** Deja en blanco los campos que el archivo trae inservibles, en todas las filas. */
  const vaciarInvalidos = () => {
    const porClave = new Map(filasTabla.map((f) => [f.id, f.camposSaneados]));
    setFilas((prev) =>
      prev.map((f) => {
        const campos = porClave.get(`${f.origen}:${f.fila}`);
        if (!campos?.length) return f;
        return campos.reduce((acc, campo) => ({ ...acc, [campo]: null }), { ...f });
      })
    );
  };

  /** Filas del archivo descartadas en la revisión previa: la carga las excluye. */
  const filasDescartadas = () =>
    filasTabla.filter((f) => f.descartada && f.origen === 'excel').map((f) => f.fila);

  const procesadas = resumenBackend?.procesadas ?? 0;
  const mensajeResultado =
    procesadas > 0
      ? `Se cargaron ${procesadas} personas en la encuesta.` +
        (totalConError > 0 ? ` ${totalConError} filas con error no se cargaron.` : '')
      : 'No se cargó ninguna persona. Revisa el detalle para ver por qué.';

  const descartarPendientes = () =>
    setDescartadas((prev) => new Set([...prev, ...pendientes.map((f) => f.id)]));

  const columnas = useMemo(() => {
    const texto = (field, headerName, width) => ({
      field,
      headerName,
      width,
      sortable: false,
      renderCell: ({ row, field: campo }) => (
        <CeldaEditable
          value={row[campo]}
          disabled={soloLectura}
          invalido={row.camposSaneados.includes(campo)}
          onCommit={(valor) => editarCelda({ id: row.id, field: campo, value: valor })}
        />
      ),
    });

    return [
      { field: 'fila', headerName: 'Fila', width: 75 },
      {
        field: 'origen',
        headerName: 'Origen',
        width: 95,
        valueGetter: ({ value }) => ORIGEN_LABEL[value] ?? value,
      },
      {
        field: 'estado',
        headerName: 'Qué pasa',
        width: 125,
        renderCell: ({ value }) => (
          <Chip
            size="small"
            variant="outlined"
            color={ESTADO[value].color}
            label={etiquetaEstado(value, soloLectura)}
          />
        ),
      },
      texto('documento', 'Documento', 150),
      texto('correo', 'Correo', 230),
      texto('indicativo', 'Ind.', 85),
      texto('celular', 'Celular', 130),
      texto('uidEmpresa', 'UidEmpresa', 125),
      texto('uid2', 'Uid2', 110),
      texto('uid3', 'Uid3', 110),
      { field: 'detalle', headerName: 'Detalle', flex: 1, minWidth: 300 },
      {
        field: 'descartada',
        headerName: 'Descartar',
        width: 110,
        sortable: false,
        renderCell: ({ row }) => (
          <Tooltip title={row.descartada ? 'Volver a incluir la fila' : 'No cargar esta fila'}>
            {/* En el resultado la carga ya ocurrió: descartar ahí no cambia nada. */}
            <span>
              <Switch
                size="small"
                color="error"
                checked={row.descartada}
                disabled={esResultado}
                onChange={() => alternarDescarte(row.id)}
              />
            </span>
          </Tooltip>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soloLectura]);

  // ── Pasos ──────────────────────────────────────────────────────
  const pasos = useMemo(() => {
    const disponibles = ['revisar'];
    if (groups.length > 0) disponibles.push('duplicados');
    disponibles.push('resumen');
    return disponibles;
  }, [groups.length]);

  const paso = pasos[pasoActual];
  const esUltimo = pasoActual === pasos.length - 1;

  const tituloPaso = {
    revisar: soloLectura
      ? recortadas > 0
        ? `Detalle (${detalle.length} de ${detalle.length + recortadas})`
        : `Detalle (${detalle.length})`
      : `Revisar filas (${conteos.todas})`,
    duplicados: `Duplicados (${groups.length})`,
    resumen: soloLectura ? 'Resumen' : 'Confirmación',
  };

  const seleccionDe = (grupo) => selecciones[grupo.id] ?? grupo.sugerida;
  const gruposResueltos = groups.filter((g) => selecciones[g.id] !== undefined).length;

  const handleSeleccion = (grupoId, valor) =>
    setSelecciones((prev) => ({ ...prev, [grupoId]: valor }));

  const resolverTodos = (estrategia) =>
    setSelecciones(
      groups.reduce((acc, grupo) => {
        acc[grupo.id] = estrategia === 'primera' ? grupo.filas[0].key : grupo.sugerida;
        return acc;
      }, {})
    );

  const gruposPagina = groups.slice(
    (paginaGrupos - 1) * GRUPOS_POR_PAGINA,
    paginaGrupos * GRUPOS_POR_PAGINA
  );

  const cerrar = () => {
    if (isLoading) return;
    setSelecciones({});
    setPasoActual(0);
    setPaginaGrupos(1);
    onClose();
  };

  return (
    <Dialog
      open={open}
      maxWidth="xl"
      fullWidth
      onClose={cerrar}
      className={styles.AudienceIssuesDialog}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {esResultado ? 'Resultado de la carga' : 'Revisión de la audiencia'}
        <Chip size="small" variant="outlined" label={VERSION_UI} />
      </DialogTitle>

      <DialogContent dividers sx={{ position: 'relative' }}>
        {isLoading && (
          <Box className={styles.overlay}>
            <CircularProgress />
            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Cargando la audiencia…
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Con archivos grandes puede tardar un par de minutos. No cierres esta ventana.
            </Typography>
          </Box>
        )}

        {headerError ? (
          <Alert severity="error" icon={<ErrorOutlineIcon />}>
            {headerError}
          </Alert>
        ) : (
          <>
            {esResultado && (
              <Alert severity={procesadas > 0 ? 'success' : 'error'} sx={{ mb: 2 }}>
                {mensajeResultado}
              </Alert>
            )}

            <Stepper activeStep={pasoActual} sx={{ mb: 3 }}>
              {pasos.map((nombre, indice) => (
                <Step key={nombre} completed={indice < pasoActual}>
                  <StepLabel onClick={() => setPasoActual(indice)} sx={{ cursor: 'pointer' }}>
                    {tituloPaso[nombre]}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            {paso === 'revisar' && (
              <>
                {!soloLectura && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Escribe directo en cualquier celda para corregirla. Las celdas en rojo traen
                    un dato que no sirve para identificar (por ejemplo el error #N/D de un
                    BUSCARV): puedes escribir el valor bueno o vaciarla con el botón de la propia
                    celda. Con el interruptor de la derecha no se carga esa fila. Todo se valida
                    al instante y <strong>no modifica tu archivo de Excel</strong>.
                  </Alert>
                )}

                <Box className={styles.resumen}>
                  {FILTROS.map((f) => (
                    <Chip
                      key={f.id}
                      size="small"
                      label={`${f.label} (${conteos[f.id === 'todas' ? 'todas' : f.id]})`}
                      color={ESTADO[f.id]?.color ?? 'default'}
                      variant={filtro === f.id ? 'filled' : 'outlined'}
                      onClick={() => setFiltro(f.id)}
                    />
                  ))}
                </Box>

                {pendientes.length > 0 && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    Hay <strong>{pendientes.length} filas en rojo</strong> que no se pueden
                    cargar tal cual. Corrige la celda, vacíala si el dato no sirve, o descarta la
                    fila. Nada se carga hasta que no quede ninguna.
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                      <Button size="small" variant="outlined" onClick={vaciarInvalidos}>
                        {`Vaciar los datos inválidos (${conteos.invalido})`}
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={descartarPendientes}
                      >
                        {`Descartar las filas en rojo (${pendientes.length})`}
                      </Button>
                    </Box>
                  </Alert>
                )}

                <div className={styles.grid}>
                  <DataGrid
                    rows={filasFiltradas}
                    columns={columnas}
                    density="compact"
                    disableSelectionOnClick
                    components={{ Toolbar }}
                    rowsPerPageOptions={[25, 50, 100]}
                    initialState={{ pagination: { pageSize: 25 } }}
                    getRowClassName={({ row }) => ESTADO[row.estado].clase}
                  />
                </div>
              </>
            )}

            {paso === 'duplicados' && (
              <>
                <Alert severity="warning" icon={<ContentCopyIcon />} sx={{ mb: 2 }}>
                  Estas filas comparten documento, correo, celular o UidEmpresa, así que el
                  sistema las tomaría como la misma persona. Elige cuál se debe cargar.
                </Alert>
                <Box className={styles.resumen}>
                  <Chip
                    size="small"
                    color={gruposResueltos === groups.length ? 'success' : 'default'}
                    label={`${gruposResueltos} de ${groups.length} grupos revisados`}
                  />
                  <Button size="small" onClick={() => resolverTodos('completa')}>
                    Conservar la más completa de cada grupo
                  </Button>
                  <Button size="small" onClick={() => resolverTodos('primera')}>
                    Conservar la primera de cada grupo
                  </Button>
                </Box>
                <Box className={styles.grupos}>
                  {gruposPagina.map((grupo) => (
                    <GrupoDuplicado
                      key={grupo.id}
                      grupo={grupo}
                      seleccion={seleccionDe(grupo)}
                      onChange={handleSeleccion}
                    />
                  ))}
                </Box>
                {groups.length > GRUPOS_POR_PAGINA && (
                  <Pagination
                    sx={{ mt: 1 }}
                    size="small"
                    count={Math.ceil(groups.length / GRUPOS_POR_PAGINA)}
                    page={paginaGrupos}
                    onChange={(_, valor) => setPaginaGrupos(valor)}
                  />
                )}
              </>
            )}

            {paso === 'resumen' && (
              <Box className={styles.resumen}>
                {soloLectura ? (
                  <>
                    <Chip label={`${resumenBackend?.totalFilas ?? 0} filas recibidas`} />
                    <Chip
                      color="success"
                      label={`${resumenBackend?.procesadas ?? 0} ${esPrevio ? 'se cargarian' : 'procesadas'}`}
                    />
                    <Chip color="warning" label={`${resumenBackend?.sinEmail ?? 0} sin correo`} />
                    {(resumenBackend?.correosInvalidos ?? 0) > 0 && (
                      <Chip
                        color="warning"
                        variant="outlined"
                        label={`${resumenBackend.correosInvalidos} con correo invalido`}
                      />
                    )}
                    <Chip label={`${resumenBackend?.yaRespondidas ?? 0} ya respondidas`} />
                    {/* El total, no el largo de la lista: el detalle viene recortado. */}
                    <Chip color="error" label={`${totalConError} con error`} />
                  </>
                ) : (
                  <>
                    <Chip label={`${conteos.todas} filas leídas`} />
                    <Chip color="error" label={`${conteos.invalido} con dato inválido`} />
                    <Chip color="error" variant="outlined" label={`${conteos.error} sin identificar`} />
                    <Chip label={`${conteos.descartada} descartadas por ti`} />
                    <Chip color="warning" label={`${conteos.advertencia} con advertencia`} />
                    <Chip color="warning" variant="outlined" label={`${groups.length} grupos duplicados`} />
                    <Chip color="success" label={`${filasFinales.length} se van a cargar`} />
                  </>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        {esResultado || headerError ? (
          <Button onClick={cerrar} variant="contained">
            Cerrar
          </Button>
        ) : esPrevio ? (
          <>
            <Button color="inherit" onClick={onCorregir} disabled={isLoading}>
              Descartar archivo
            </Button>
            <Box sx={{ flex: 1 }} />
            {conErrores ? (
              // Con errores no se carga de largo: quien carga tiene que decidir. O
              // corrige el archivo, o acepta explicitamente que esas filas queden fuera
              // de la encuesta. Antes se omitian solas y nadie se enteraba.
              <Button
                variant="contained"
                color="warning"
                disabled={isLoading}
                onClick={() => onConfirmar('omitir', filasDescartadas())}
              >
                {`Omitir ${totalConError} con error y cargar el resto`}
              </Button>
            ) : (
              <Button
                variant="contained"
                disabled={isLoading || (resumenBackend?.procesadas ?? 0) === 0}
                onClick={() => onConfirmar(null, filasDescartadas())}
              >
                {`Cargar ${resumenBackend?.procesadas ?? 0} filas`}
              </Button>
            )}
          </>
        ) : (
          <>
            <Button color="inherit" onClick={onCorregir} disabled={isLoading}>
              Descartar archivo
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              onClick={() => setPasoActual((p) => p - 1)}
              disabled={pasoActual === 0 || isLoading}
            >
              Atrás
            </Button>
            {!esUltimo ? (
              <Button variant="contained" onClick={() => setPasoActual((p) => p + 1)}>
                Siguiente
              </Button>
            ) : (
              <Button
                variant="contained"
                color={pendientes.length > 0 ? 'error' : 'primary'}
                disabled={pendientes.length > 0 || filasFinales.length === 0 || isLoading}
                onClick={() => onConfirmar(filasFinales)}
              >
                {pendientes.length > 0
                  ? `Resolver ${pendientes.length} filas en rojo`
                  : `Cargar ${filasFinales.length} filas`}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

AudienceIssuesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  modo: PropTypes.oneOf(['validacion', 'previo', 'resultado']).isRequired,
  filasIniciales: PropTypes.array,
  analisisResultado: PropTypes.object,
  headerError: PropTypes.string,
  resumenBackend: PropTypes.object,
  isLoading: PropTypes.bool,
  onCorregir: PropTypes.func.isRequired,
  onConfirmar: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

AudienceIssuesDialog.defaultProps = {
  filasIniciales: [],
  analisisResultado: null,
  headerError: null,
  resumenBackend: null,
  isLoading: false,
};

export default AudienceIssuesDialog;
